import { read, utils } from "xlsx"
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import Big from 'big.js'
import { BE_STOCK_LIST_URL, STOCK_LIST_HEADERS, STOCK_LIST__DISPLAY_HEADERS } from "./constants"

export async function readSheet(url) {
    if (!url) {
        throw new Error("Missing BE_STOCK_LIST_URL (REACT_APP_BE_STOCK_LIST_URL)")
    }
    const response = await fetch(url)
    if (!response.ok) {
        throw new Error(`Failed to fetch stock list: ${response.status} ${response.statusText}`)
    }
    const csvText = await response.text()
    const wb = read(csvText, { type: "string" })
    const ws = wb.Sheets[wb.SheetNames[0]]
    return ws
}

export function getStockListDataFromSheet(ws) {
    return utils.sheet_to_json(ws, { header: STOCK_LIST_HEADERS })
}

export async function fetchData() {
    const ws = await readSheet(BE_STOCK_LIST_URL)
    const data = getStockListDataFromSheet(ws)
        .map(d => d.cut ? d : ({ ...d, cut: '--' })) // undefined cuts are fancy shapes
        .map(d => ({
            ...d,
            stock_no: d.stock_no?.toString().toUpperCase(),
            report_no: d.report_no?.toString().toUpperCase()
        }))
    return data
}

export async function downloadBEList(data) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('BE List');

    // Constants from Python script logic
    const FONT_NAME = 'Times New Roman';
    const FONT_SIZE = 11;

    // Title 1
    const title1 = worksheet.getCell('A1');
    title1.value = 'BOTHRA ENTERPRISES';
    title1.font = { name: FONT_NAME, bold: true, size: 14 };
    worksheet.getRow(1).height = 25;

    // Title 2
    const title2 = worksheet.getCell('A2');
    title2.value = 'All NET Price, Final';
    title2.font = { name: FONT_NAME, bold: true };

    // Table Headers (Row 4)
    const displayHeaders = STOCK_LIST__DISPLAY_HEADERS[3];
    const headerRow = worksheet.getRow(4);
    headerRow.values = displayHeaders;
    headerRow.eachCell((cell) => {
        cell.font = { name: FONT_NAME, bold: true, color: { argb: 'FFFFFFFF' }, size: FONT_SIZE };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF8B0000' }
        };
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
        cell.alignment = { horizontal: 'center' };
    });

    // Add Data
    data.forEach((item, index) => {
        const rowData = [
            index + 1,
            item.stock_no,
            item.shape,
            parseFloat(item.ct),
            item.color,
            item.clarity,
            item.cut,
            item.polish,
            item.symm,
            item.fluor,
            parseFloat(item.depth),
            parseFloat(item.table),
            item.measurements,
            item.lab,
            item.report_no,
            item.comments,
            parseFloat(item.rap),
            parseFloat(item.disc) / 100, // Frontend disc is usually like -70, Python expecting decimals
            parseFloat(item.per_ct),
            parseFloat(item.total_dollar)
        ];
        const row = worksheet.addRow(rowData);
        
        row.eachCell((cell, colNumber) => {
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
            
            // Default Font
            cell.font = { name: FONT_NAME, size: FONT_SIZE };

            // Conditional Bold
            const isEX = ['EX'].includes(cell.value);
            const isCutPolishSymm = colNumber >= 7 && colNumber <= 9; // G, H, I
            const isLab = colNumber === 14; // N
            
            if ((isCutPolishSymm && isEX) || isLab) {
                cell.font.bold = true;
            }

            // Alignment
            if (colNumber === 2) { // Stock No
                cell.alignment = { horizontal: 'right' };
            } else if (colNumber === 3 || (colNumber >= 5 && colNumber <= 16)) { // Shape, Color to Comments
                cell.alignment = { horizontal: 'center' };
            }

            // Number Formats
            if (colNumber === 4) cell.numFmt = '0.00'; // Ct
            if (colNumber === 11) cell.numFmt = '0.0'; // Depth
            if (colNumber === 15) cell.numFmt = '0'; // Report No
            if (colNumber === 18) cell.numFmt = '0.00%'; // Disc
            if (colNumber === 19) cell.numFmt = '0.00'; // Per Ct
            if (colNumber === 20) cell.numFmt = '0.00'; // Total $
            
            // Report No smaller font
            if (colNumber === 15) cell.font.size = 9;
        });
    });

    // Add Totals
    const totalsRowIndex = 5 + data.length;
    const totalWt = data.reduce((sum, d) => sum.plus(d.ct), Big(0));
    const totalDollar = data.reduce((sum, d) => sum.plus(d.total_dollar), Big(0));

    const totalRow = worksheet.getRow(totalsRowIndex);
    // Add borders to the entire row first for consistency
    for (let i = 1; i <= displayHeaders.length; i++) {
        totalRow.getCell(i).border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    }

    const totalWtLabelCell = worksheet.getCell(`C${totalsRowIndex}`);
    totalWtLabelCell.value = 'Total Wt';
    totalWtLabelCell.alignment = { horizontal: 'right' };
    totalWtLabelCell.font = { name: FONT_NAME, size: FONT_SIZE };

    const totalWtValueCell = worksheet.getCell(`D${totalsRowIndex}`);
    totalWtValueCell.value = parseFloat(totalWt.toFixed(2));
    totalWtValueCell.font = { name: FONT_NAME, bold: true, size: FONT_SIZE };
    totalWtValueCell.numFmt = '0.00';

    const totalDollarLabelCell = worksheet.getCell(`S${totalsRowIndex}`);
    totalDollarLabelCell.value = 'Total $';
    totalDollarLabelCell.alignment = { horizontal: 'right' };
    totalDollarLabelCell.font = { name: FONT_NAME, size: FONT_SIZE };

    const totalDollarValueCell = worksheet.getCell(`T${totalsRowIndex}`);
    totalDollarValueCell.value = parseFloat(totalDollar.toFixed(2));
    totalDollarValueCell.font = { name: FONT_NAME, bold: true, size: FONT_SIZE };
    totalDollarValueCell.numFmt = '0.00';

    // Column Widths (matching Python exactly where specified)
    worksheet.getColumn('A').width = 6;
    worksheet.getColumn('B').width = 12; // Stock No (custom)
    worksheet.getColumn('C').width = 6;
    worksheet.getColumn('D').width = 8;
    worksheet.getColumn('E').width = 6;
    worksheet.getColumn('F').width = 10; // Clarity
    worksheet.getColumn('G').width = 6;
    worksheet.getColumn('H').width = 6;
    worksheet.getColumn('I').width = 6;
    worksheet.getColumn('J').width = 6;
    worksheet.getColumn('K').width = 8;
    worksheet.getColumn('L').width = 8;
    worksheet.getColumn('M').width = 16;
    worksheet.getColumn('N').width = 5;
    worksheet.getColumn('O').width = 12;
    worksheet.getColumn('P').width = 8;
    worksheet.getColumn('Q').width = 8;
    worksheet.getColumn('R').width = 14; // Disc
    worksheet.getColumn('S').width = 12; // Per Ct
    worksheet.getColumn('T').width = 14; 

    // Write to buffer and save
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), 'BE List.xlsx');
}
